import type { Goal } from '../types/timeline'

export interface AiConfidenceScore {
  score: number
  slotCompleteness: number
  logicConsistency: number
  constraintAdherence: number
  missingMandatorySlots: string[]
  hasCycle: boolean
  reasoning: string[]
}

const record = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : null

const textPresent = (value: unknown) => typeof value === 'string' && value.trim().length > 0
const datePresent = (value: unknown) => (typeof value === 'string' && value.trim().length > 0) || typeof value === 'number'

const collectGraph = (goalInput: unknown) => {
  const edges = new Map<string, Set<string>>()
  const ensure = (id: string) => { if (!edges.has(id)) edges.set(id, new Set()) }
  const add = (from: unknown, to: unknown) => {
    if (typeof from !== 'string' || typeof to !== 'string' || !from || !to) return
    ensure(from); ensure(to); edges.get(from)!.add(to)
  }
  const visitChecklist = (items: unknown, parentId: string) => {
    if (!Array.isArray(items)) return
    items.forEach((item, index) => {
      const node = record(item)
      if (!node) return
      const id = typeof node.id === 'string' ? node.id : `${parentId}:check:${index}`
      add(parentId, id)
      if (typeof node.parentContainerId === 'string') add(node.parentContainerId, id)
      if (Array.isArray(node.prerequisites)) node.prerequisites.forEach((source) => add(source, id))
      if (Array.isArray(node.blockedByNodeIds)) node.blockedByNodeIds.forEach((link) => add(record(link)?.targetId, id))
    })
  }
  const visitNode = (nodeValue: unknown, parentId?: string) => {
    const node = record(nodeValue)
    if (!node) return
    const id = typeof node.id === 'string' ? node.id : ''
    if (id) ensure(id)
    if (parentId) add(parentId, id)
    if (typeof node.parentId === 'string') add(node.parentId, id)
    visitChecklist(node.checklist, id)
    if (Array.isArray(node.tasks)) node.tasks.forEach((child) => visitNode(child, id))
    if (Array.isArray(node.subTasks)) node.subTasks.forEach((child) => visitNode(child, id))
  }
  visitNode(goalInput)
  return edges
}

export const hasDirectedCycle = (goalInput: unknown): boolean => {
  const graph = collectGraph(goalInput)
  const visiting = new Set<string>()
  const visited = new Set<string>()
  const visit = (id: string): boolean => {
    if (visiting.has(id)) return true
    if (visited.has(id)) return false
    visiting.add(id)
    for (const next of graph.get(id) ?? []) if (visit(next)) return true
    visiting.delete(id); visited.add(id)
    return false
  }
  return [...graph.keys()].some(visit)
}

const inspectSlots = (goalInput: unknown) => {
  const missing: string[] = []
  let required = 0
  let complete = 0
  const check = (ok: boolean, label: string) => { required += 1; if (ok) complete += 1; else missing.push(label) }
  const goal = record(goalInput)
  if (!goal) return { missing: ['Goal payload'], ratio: 0 }
  check(textPresent(goal.title), 'Goal.title')
  check(datePresent(goal.startDate), 'Goal.startDate')
  check(datePresent(goal.endDate), 'Goal.endDate')
  const checklist = (items: unknown, parent: string) => Array.isArray(items) && items.forEach((item, index) => {
    const entry = record(item)
    check(Boolean(entry && (textPresent(entry.title) || textPresent(entry.text))), `${parent}.checklist[${index}].title`)
    check(true, `${parent}.checklist[${index}].parentContainerId`)
  })
  checklist(goal.checklist, 'Goal')
  if (Array.isArray(goal.tasks)) goal.tasks.forEach((taskValue, taskIndex) => {
    const task = record(taskValue)
    check(Boolean(task && textPresent(task.title)), `Task[${taskIndex}].title`)
    check(true, `Task[${taskIndex}].parentId`)
    check(Boolean(task && datePresent(task.startDate)), `Task[${taskIndex}].startDate`)
    checklist(task?.checklist, `Task[${taskIndex}]`)
    if (Array.isArray(task?.subTasks)) task.subTasks.forEach((subValue, subIndex) => {
      const sub = record(subValue)
      check(Boolean(sub && textPresent(sub.title)), `Subtask[${taskIndex}.${subIndex}].title`)
      check(true, `Subtask[${taskIndex}.${subIndex}].parentId`)
      check(Boolean(sub && datePresent(sub.startDate)), `Subtask[${taskIndex}.${subIndex}].startDate`)
      checklist(sub?.checklist, `Subtask[${taskIndex}.${subIndex}]`)
    })
  })
  return { missing, ratio: required === 0 ? 0 : complete / required }
}

export const scoreAiTimelineResult = (
  goal: Goal | unknown,
  softWarnings: string[],
): AiConfidenceScore => {
  const slots = inspectSlots(goal)
  const hasCycle = hasDirectedCycle(goal)
  const warningKeys = new Set(softWarnings.map((warning) => warning.split(/\s+/)[0]?.toLowerCase()).filter(Boolean))
  const slotCompleteness = slots.ratio
  const logicConsistency = hasCycle ? 0 : 1
  const constraintAdherence = Math.max(0, 1 - warningKeys.size * 0.1)
  const weighted = slotCompleteness * 0.5 + logicConsistency * 0.3 + constraintAdherence * 0.2
  const penalty = (slots.missing.length > 0 ? 0.2 : 0) + (hasCycle ? 0.3 : 0) + warningKeys.size * 0.1
  const score = Math.max(0, Math.min(1, weighted - penalty))
  const reasoning = [
    `Slot completeness: ${Math.round(slotCompleteness * 100)}%.`,
    hasCycle ? 'Logical consistency: a dependency cycle was detected.' : 'Logical consistency: no dependency cycle was detected.',
    warningKeys.size > 0
      ? `Constraint adherence: ${warningKeys.size} unique soft warning${warningKeys.size === 1 ? '' : 's'}.`
      : 'Constraint adherence: no soft warnings.',
  ]
  return { score, slotCompleteness, logicConsistency, constraintAdherence, missingMandatorySlots: slots.missing, hasCycle, reasoning }
}

export const combineAiConfidence = (routerConfidence: number, resultConfidence: number): number =>
  Math.max(0, Math.min(1, routerConfidence * 0.6 + resultConfidence * 0.4))
